import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 16px 0',
  },
  svgContainer: {
    width: '100%',
    overflow: 'visible',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    backgroundColor: '#1f2937',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.5',
    zIndex: 9999,
    maxWidth: '260px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
  },
  tableContainer: {
    marginTop: '20px',
  },
  tableTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
  },
  th: {
    textAlign: 'left',
    padding: '8px 10px',
    borderBottom: '2px solid #e5e7eb',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  td: {
    padding: '7px 10px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
  },
  surpriseBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 600,
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '14px',
  },
};

const pulseKeyframes = `
@keyframes surprise-pulse {
  0%, 100% { filter: drop-shadow(0 0 3px rgba(239, 68, 68, 0.4)); }
  50% { filter: drop-shadow(0 0 8px rgba(239, 68, 68, 0.8)); }
}
`;

function getBarColor(value, threshold) {
  if (value > threshold) return '#ef4444';
  if (value >= 1) return '#f59e0b';
  return '#9ca3af';
}

function getSurpriseBadgeStyle(value, threshold) {
  if (value > threshold) {
    return { ...styles.surpriseBadge, backgroundColor: '#fef2f2', color: '#dc2626' };
  }
  if (value >= 1) {
    return { ...styles.surpriseBadge, backgroundColor: '#fffbeb', color: '#d97706' };
  }
  return { ...styles.surpriseBadge, backgroundColor: '#f3f4f6', color: '#6b7280' };
}

export default function PriceSurpriseIndex({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const hasData = useMemo(() => {
    return data && data.dates && data.dates.length > 0 && data.surpriseIndex && data.surpriseIndex.length > 0;
  }, [data]);

  const topFive = useMemo(() => {
    if (!data || !data.topSurprises) return [];
    return data.topSurprises.slice(0, 5);
  }, [data]);

  const threshold = useMemo(() => {
    return data && data.threshold != null ? data.threshold : 2.0;
  }, [data]);

  useEffect(() => {
    if (!hasData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 50, left: 45 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const { dates, surpriseIndex } = data;
    const barData = dates.map((date, i) => ({
      date,
      value: surpriseIndex[i] != null ? surpriseIndex[i] : 0,
      index: i,
    }));

    const xScale = d3
      .scaleBand()
      .domain(barData.map((d) => d.date))
      .range([0, innerWidth])
      .padding(0.2);

    const maxVal = d3.max(barData, (d) => d.value) || 1;
    const yMax = Math.max(maxVal * 1.15, threshold * 1.3);

    const yScale = d3.scaleLinear().domain([0, yMax]).range([innerHeight, 0]).nice();

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Gridlines
    g.append('g')
      .attr('class', 'grid')
      .call(
        d3
          .axisLeft(yScale)
          .ticks(5)
          .tickSize(-innerWidth)
          .tickFormat('')
      )
      .selectAll('line')
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    g.select('.grid .domain').remove();

    // Threshold line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(threshold))
      .attr('y2', yScale(threshold))
      .attr('stroke', '#ef4444')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4')
      .attr('opacity', 0.7);

    g.append('text')
      .attr('x', innerWidth - 4)
      .attr('y', yScale(threshold) - 6)
      .attr('text-anchor', 'end')
      .attr('fill', '#ef4444')
      .attr('font-size', '10px')
      .attr('font-weight', 500)
      .text(`threshold: ${threshold}`);

    // Find top surprise address for each bar (for tooltip)
    const topSurprisesByDate = {};
    if (data.topSurprises) {
      data.topSurprises.forEach((s) => {
        if (!topSurprisesByDate[s.date] || s.surprise > topSurprisesByDate[s.date].surprise) {
          topSurprisesByDate[s.date] = s;
        }
      });
    }

    // Bars
    g.selectAll('.bar')
      .data(barData)
      .enter()
      .append('rect')
      .attr('class', (d) => (d.value > threshold ? 'bar surprise-bar' : 'bar'))
      .attr('x', (d) => xScale(d.date))
      .attr('y', (d) => yScale(d.value))
      .attr('width', xScale.bandwidth())
      .attr('height', (d) => Math.max(0, innerHeight - yScale(d.value)))
      .attr('fill', (d) => getBarColor(d.value, threshold))
      .attr('rx', 2)
      .attr('ry', 2)
      .style('cursor', 'pointer')
      .style('animation', (d) => (d.value > threshold ? 'surprise-pulse 2s ease-in-out infinite' : 'none'))
      .on('mouseenter', function (event, d) {
        d3.select(this).attr('opacity', 0.8);
        const topAddr = topSurprisesByDate[d.date];
        setTooltip({
          x: event.clientX,
          y: event.clientY,
          date: d.date,
          value: d.value,
          topAddress: topAddr ? topAddr.label : null,
        });
      })
      .on('mousemove', function (event) {
        setTooltip((prev) => (prev ? { ...prev, x: event.clientX, y: event.clientY } : null));
      })
      .on('mouseleave', function () {
        d3.select(this).attr('opacity', 1);
        setTooltip(null);
      });

    // X-axis
    const xAxis = g
      .append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale));

    xAxis.select('.domain').attr('stroke', '#d1d5db');
    xAxis.selectAll('.tick line').attr('stroke', '#d1d5db');
    xAxis
      .selectAll('.tick text')
      .attr('fill', '#6b7280')
      .attr('font-size', '10px')
      .attr('transform', 'rotate(-40)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Reduce tick labels if too many
    if (barData.length > 15) {
      const interval = Math.ceil(barData.length / 12);
      xAxis.selectAll('.tick text').attr('visibility', (d, i) => (i % interval === 0 ? 'visible' : 'hidden'));
    }

    // Y-axis
    const yAxis = g.append('g').call(d3.axisLeft(yScale).ticks(5));

    yAxis.select('.domain').attr('stroke', '#d1d5db');
    yAxis.selectAll('.tick line').attr('stroke', '#d1d5db');
    yAxis.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', '10px');

    // Y-axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -35)
      .attr('text-anchor', 'middle')
      .attr('fill', '#9ca3af')
      .attr('font-size', '11px')
      .text('Surprise Index');
  }, [data, hasData, threshold]);

  if (!hasData) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Price Surprise Index</h3>
        <p style={styles.subtitle}>Unexpected price deviations from recent trends</p>
        <div style={styles.emptyState}>No surprise index data available.</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <style>{pulseKeyframes}</style>
      <h3 style={styles.title}>Price Surprise Index</h3>
      <p style={styles.subtitle}>Unexpected price deviations from recent trends</p>

      <div style={styles.svgContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 300"
          preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: 'auto' }}
        />
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x + 12,
            top: tooltip.y - 10,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{tooltip.date}</div>
          <div>
            Surprise: <strong>{tooltip.value.toFixed(2)}</strong>
          </div>
          {tooltip.topAddress && (
            <div style={{ marginTop: '4px', color: '#d1d5db', fontSize: '11px' }}>
              Top contributor: {tooltip.topAddress}
            </div>
          )}
        </div>
      )}

      {topFive.length > 0 && (
        <div style={styles.tableContainer}>
          <div style={styles.tableTitle}>Top Surprises</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Address</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Field</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Expected</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actual</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Surprise</th>
              </tr>
            </thead>
            <tbody>
              {topFive.map((item, i) => (
                <tr key={`${item.addressId}-${item.date}-${i}`}>
                  <td style={styles.td}>{item.label}</td>
                  <td style={styles.td}>{item.date}</td>
                  <td style={styles.td}>
                    <code
                      style={{
                        backgroundColor: '#f3f4f6',
                        padding: '1px 5px',
                        borderRadius: '3px',
                        fontSize: '11px',
                      }}
                    >
                      {item.field}
                    </code>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    ${item.expected != null ? item.expected.toFixed(2) : '--'}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    ${item.actual != null ? item.actual.toFixed(2) : '--'}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <span style={getSurpriseBadgeStyle(item.surprise, threshold)}>
                      {item.surprise.toFixed(2)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
